const textEncoder = new TextEncoder();

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (data) => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const toDosTime = (date) => {
  const dt = date || new Date();
  const year = dt.getFullYear();
  const month = dt.getMonth() + 1;
  const day = dt.getDate();
  const hours = dt.getHours();
  const minutes = dt.getMinutes();
  const seconds = Math.floor(dt.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | (month << 5) | day;
  const dosTime = (hours << 11) | (minutes << 5) | seconds;
  return { dosDate, dosTime };
};

const buildUint32LE = (value) => {
  const buffer = new Uint8Array(4);
  buffer[0] = value & 0xff;
  buffer[1] = (value >>> 8) & 0xff;
  buffer[2] = (value >>> 16) & 0xff;
  buffer[3] = (value >>> 24) & 0xff;
  return buffer;
};

const buildUint16LE = (value) => {
  const buffer = new Uint8Array(2);
  buffer[0] = value & 0xff;
  buffer[1] = (value >>> 8) & 0xff;
  return buffer;
};

const concatBuffers = (buffers) => {
  const totalLength = buffers.reduce((sum, buffer) => sum + buffer.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  buffers.forEach((buffer) => {
    output.set(buffer, offset);
    offset += buffer.length;
  });
  return output;
};

const normalizeFile = (file) => {
  const name = file.name.replace(/\\/g, '/');
  if (file.data instanceof Uint8Array) {
    return { ...file, name, data: file.data };
  }
  if (typeof file.data === 'string') {
    return { ...file, name, data: textEncoder.encode(file.data) };
  }
  if (file.data instanceof ArrayBuffer) {
    return { ...file, name, data: new Uint8Array(file.data) };
  }
  return { ...file, name, data: new Uint8Array() };
};

export const buildZip = (files = []) => {
  const normalized = files.map(normalizeFile);
  let offset = 0;
  const fileRecords = [];
  const centralRecords = [];

  normalized.forEach((file) => {
    const nameBytes = textEncoder.encode(file.name);
    const data = file.data || new Uint8Array();
    const crc = crc32(data);
    const { dosDate, dosTime } = toDosTime(file.modifiedAt ? new Date(file.modifiedAt) : new Date());

    const localHeader = concatBuffers([
      buildUint32LE(0x04034b50),
      buildUint16LE(20),
      buildUint16LE(0),
      buildUint16LE(0),
      buildUint16LE(dosTime),
      buildUint16LE(dosDate),
      buildUint32LE(crc),
      buildUint32LE(data.length),
      buildUint32LE(data.length),
      buildUint16LE(nameBytes.length),
      buildUint16LE(0),
      nameBytes
    ]);

    const localRecord = concatBuffers([localHeader, data]);
    fileRecords.push(localRecord);

    const centralHeader = concatBuffers([
      buildUint32LE(0x02014b50),
      buildUint16LE(20),
      buildUint16LE(20),
      buildUint16LE(0),
      buildUint16LE(0),
      buildUint16LE(dosTime),
      buildUint16LE(dosDate),
      buildUint32LE(crc),
      buildUint32LE(data.length),
      buildUint32LE(data.length),
      buildUint16LE(nameBytes.length),
      buildUint16LE(0),
      buildUint16LE(0),
      buildUint16LE(0),
      buildUint16LE(0),
      buildUint32LE(0),
      buildUint32LE(offset),
      nameBytes
    ]);
    centralRecords.push(centralHeader);
    offset += localRecord.length;
  });

  const centralSize = centralRecords.reduce((sum, record) => sum + record.length, 0);
  const centralOffset = offset;

  const endRecord = concatBuffers([
    buildUint32LE(0x06054b50),
    buildUint16LE(0),
    buildUint16LE(0),
    buildUint16LE(normalized.length),
    buildUint16LE(normalized.length),
    buildUint32LE(centralSize),
    buildUint32LE(centralOffset),
    buildUint16LE(0)
  ]);

  return new Blob([...fileRecords, ...centralRecords, endRecord], {
    type: 'application/zip'
  });
};
